import { Component } from '@theme/component'
import { BundleChangeEvent, CartAddEvent, CartErrorEvent } from '@theme/events'
import { fetchConfig, parseItemCountFromSectionsHTML } from '@theme/utils'

class CustomQuantityComponent extends Component {
  #section
  #clickHandler
  #readyText
  #originalDisabled
  #currentPack = 0

  mounted() {
    this.#section = this.closest('.shopify-section')
    this.#clickHandler = this.#onDocumentClick.bind(this)
    document.addEventListener('click', this.#clickHandler, true)
    this.#initPack()
    this.#refreshState()

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#syncButton(), {
        once: true
      })
    } else {
      this.#syncButton()
    }
  }

  unmounted() {
    if (this.#clickHandler) {
      document.removeEventListener('click', this.#clickHandler, true)
    }
  }

  selectPack(event) {
    const card = event.target.closest?.('[data-pack-card]')
    if (!card) return

    const size = Number.parseInt(card.dataset.packSize ?? '', 10)
    if (Number.isNaN(size) || size === this.#currentPack) return

    this.#activatePack(card, size)
  }

  increaseFlavor(event) {
    const input = this.#inputFor(event.target)
    if (!input) return
    if (this.#total() >= this.#targetCount()) return
    if (this.#value(input) >= this.#stockMax(input)) return

    input.value = this.#value(input) + 1
    this.#refreshState()
  }

  decreaseFlavor(event) {
    const input = this.#inputFor(event.target)
    if (!input) return
    if (this.#value(input) <= 0) return

    input.value = this.#value(input) - 1
    this.#refreshState()
  }

  #initPack() {
    const card =
      this.querySelector('[data-pack-card].active') ??
      this.querySelector('[data-pack-card][data-pack-size="2"]') ??
      this.querySelector('[data-pack-card]')
    if (!card) return

    const size = Number.parseInt(card.dataset.packSize ?? '', 10)
    if (Number.isNaN(size)) return

    this.#currentPack = 0
    this.#activatePack(card, size)
  }

  #activatePack(card, size) {
    for (const other of this.querySelectorAll('[data-pack-card]')) {
      const active = other === card
      other.classList.toggle('active', active)
      other
        .querySelector('[data-pack-trigger]')
        ?.setAttribute('aria-pressed', String(active))
    }

    for (const radio of this.querySelectorAll(
      'input[name="bundle_pack_size"]'
    )) {
      radio.checked = radio.value === String(size)
    }

    this.#currentPack = size
    this.#syncVariantsVisibility()
    this.#resetSelections()
    this.#refreshState()
  }

  #syncVariantsVisibility() {
    const variants = this.querySelector('[data-variants-section]')
    variants?.classList.toggle('is-collapsed', this.#currentPack <= 1)
  }

  #onDocumentClick(event) {
    const button = event.target.closest?.('[ref="addToCartButton"]')
    if (!(button && this.#section?.contains(button))) return
    if (this.#delegateToButtons()) return

    event.preventDefault()
    if (!this.#isReady()) return
    this.#submitBundle()
  }

  #refreshState() {
    const target = this.#targetCount()
    const total = this.#total()
    const isFull = total >= target

    const counter = this.querySelector('[data-bundle-counter]')
    if (counter) counter.textContent = `(${total}/${target} Selecionados)`

    for (const item of this.querySelectorAll('[data-bundle-item]')) {
      const input = item.querySelector('[data-flavor-input]')
      if (!input) continue

      const value = this.#value(input)
      this.#toggle(item.querySelector('[data-qty-action="minus"]'), value <= 0)
      this.#toggle(
        item.querySelector('[data-qty-action="plus"]'),
        isFull || value >= this.#stockMax(input)
      )
    }

    this.#syncButton()
    this.#updatePrice()
    this.dispatchEvent(new BundleChangeEvent(total, target))
  }

  #syncButton() {
    const button = this.#addButton()
    if (!button) return

    const label = this.#buttonLabel(button)
    if (this.#readyText == null && label) {
      this.#readyText = label.textContent.trim()
    }
    if (this.#originalDisabled === undefined) {
      this.#originalDisabled = button.disabled
    }

    if (this.#delegateToButtons()) {
      if (label) label.textContent = this.#readyText
      button.disabled = this.#originalDisabled
      button.classList.toggle('disabled', this.#originalDisabled)
      button.setAttribute('aria-disabled', String(this.#originalDisabled))
      return
    }

    const ready = this.#isReady()
    button.disabled = !ready
    button.classList.toggle('disabled', !ready)
    button.setAttribute('aria-disabled', String(!ready))

    if (!label) return
    if (ready) {
      label.textContent = this.#readyText
    } else {
      const remaining = Math.max(this.#targetCount() - this.#total(), 0)
      label.textContent = `Selecione mais ${remaining}`
    }
  }

  async #submitBundle() {
    const items = this.#selectedItems()
    if (!items.length) return

    const formData = new FormData()
    items.forEach((item, index) => {
      formData.append(`items[${index}][id]`, String(item.id))
      formData.append(`items[${index}][quantity]`, String(item.quantity))
    })

    const sections = this.#cartSectionIds()
    if (sections.length) formData.append('sections', sections.join(','))
    formData.append('sections_url', window.location.pathname)

    try {
      const config = fetchConfig('javascript', { body: formData })
      const response = await fetch(Theme.routes.cart_add_url, { ...config })
      const data = await response.json()
      if (!response.ok || data.status) {
        throw new Error(
          data.description || data.message || 'Erro ao adicionar ao carrinho.'
        )
      }

      this.#hideError()
      this.dispatchEvent(
        new CartAddEvent(data, this.id || 'custom-quantity', {
          itemCount: this.#resolveItemCount(data, items),
          sections: data.sections,
          source: 'custom-quantity'
        })
      )
      this.#resetSelections()
      this.#refreshState()
    } catch (error) {
      this.#dispatchError(error.message)
      this.#showError(error.message)
    }
  }

  #dispatchError(message) {
    this.dispatchEvent(
      new CartErrorEvent(this.id || 'custom-quantity', message, message)
    )
    this.dispatchEvent(
      new CartAddEvent({}, this.id || 'custom-quantity', {
        didError: true,
        itemCount: this.#currentCartCount(),
        source: 'custom-quantity'
      })
    )
  }

  #updatePrice() {
    const container = this.#section?.querySelector('[ref="priceContainer"]')
    if (!container) return

    const { price, compare } = this.#computeTotal()
    const onSale = compare > 0 && compare > price
    container.classList.toggle('price--on-sale', onSale)
    const salePrice = container.querySelector('.price__sale')
    const regularPrice = container.querySelector('.price__regular')
    const primaryPrice = salePrice || regularPrice

    if (primaryPrice) {
      primaryPrice.textContent = this.#formatMoney(price)
      primaryPrice.classList.toggle('price__sale', onSale)
      primaryPrice.classList.toggle('price__regular', !onSale)
    }

    const comparePrice =
      container.querySelector('.price__compare') ||
      (onSale ? this.#createComparePrice(primaryPrice) : null)
    if (comparePrice) {
      comparePrice.textContent = onSale ? this.#formatMoney(compare) : ''
      comparePrice.hidden = !onSale
      comparePrice.style.display = onSale ? '' : 'none'
    }
  }

  #createComparePrice(primaryPrice) {
    if (!(primaryPrice instanceof HTMLElement)) return null

    const comparePrice = document.createElement('span')
    comparePrice.className = 'price__compare'
    comparePrice.hidden = true
    primaryPrice.insertAdjacentElement('afterend', comparePrice)
    return comparePrice
  }

  #computeTotal() {
    const unit = this.#unitPrice()
    const unitCompare = this.#unitCompare()
    if (this.#currentPack <= 1) return { compare: unitCompare, price: unit }

    const { baseCompare, basePrice } = this.#computeBaseTotals(
      unit,
      unitCompare
    )
    return this.#applyPackDiscount(basePrice, baseCompare)
  }

  #computeBaseTotals(unit, unitCompare) {
    let basePrice = 0
    let baseCompare = 0
    let count = 0

    for (const item of this.querySelectorAll('[data-bundle-item]')) {
      const input = item.querySelector('[data-flavor-input]')
      const qty = input ? this.#value(input) : 0
      if (qty <= 0) continue

      const itemPrice = Number.parseInt(item.dataset.price ?? '', 10) || unit
      const itemCompare = Number.parseInt(item.dataset.compare ?? '', 10) || 0
      basePrice += qty * itemPrice
      baseCompare += qty * Math.max(itemCompare, itemPrice)
      count += qty
    }

    return this.#includeRemainingItems(
      basePrice,
      baseCompare,
      count,
      unit,
      unitCompare
    )
  }

  #includeRemainingItems(basePrice, baseCompare, count, unit, unitCompare) {
    const remaining = Math.max(this.#currentPack - count, 0)
    if (remaining <= 0) return { baseCompare, basePrice }

    const extraCompare = unitCompare > unit ? unitCompare : unit
    return {
      baseCompare: baseCompare + remaining * extraCompare,
      basePrice: basePrice + remaining * unit
    }
  }

  #applyPackDiscount(basePrice, baseCompare) {
    const discountPct = this.#activeDiscountPercentage()
    if (discountPct > 0) {
      const price = Math.round(basePrice * (1 - discountPct / 100))
      return {
        compare: baseCompare > price ? baseCompare : basePrice,
        price
      }
    }

    const compare = baseCompare > basePrice ? baseCompare : 0
    return { compare, price: basePrice }
  }

  #activeDiscountPercentage() {
    const card = this.querySelector(
      `[data-pack-card][data-pack-size="${this.#currentPack}"]`
    )
    const discount = Number.parseInt(
      card?.dataset.discountPercentage ?? '0',
      10
    )
    return Number.isNaN(discount) ? 0 : discount
  }

  #formatMoney(cents) {
    return (cents / 100).toLocaleString(Theme.shop.locale, {
      currency: Theme.shop.currency,
      style: 'currency'
    })
  }

  #selectedItems() {
    const items = []
    for (const input of this.#inputs()) {
      const quantity = this.#value(input)
      const id = Number.parseInt(input.dataset.variantId ?? '', 10)
      if (quantity > 0 && !Number.isNaN(id)) {
        items.push({ id, quantity })
      }
    }
    return items
  }

  #cartSectionIds() {
    const ids = []
    for (const element of document.querySelectorAll('ui-cart-items')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) {
        ids.push(element.dataset.sectionId)
      }
    }
    return ids
  }

  #resolveItemCount(data, items) {
    if (typeof data.item_count === 'number') return data.item_count

    const fromSections = parseItemCountFromSectionsHTML(data.sections)
    if (typeof fromSections === 'number') return fromSections

    const added = items.reduce((total, item) => total + item.quantity, 0)
    return this.#currentCartCount() + added
  }

  #currentCartCount() {
    const countEl = document.querySelector('[ref="cartItemCount"]')
    const count = Number.parseInt(countEl?.textContent ?? '', 10)
    return Number.isNaN(count) ? 0 : count
  }

  #showError(message) {
    const error = this.querySelector('[data-bundle-error]')
    const text = this.querySelector('[data-bundle-error-text]')
    if (text) text.textContent = message
    if (error) error.hidden = false
  }

  #hideError() {
    const error = this.querySelector('[data-bundle-error]')
    if (error) error.hidden = true
  }

  #resetSelections() {
    for (const input of this.#inputs()) {
      input.value = '0'
    }
  }

  #delegateToButtons() {
    return this.#hasPackCards() && this.#currentPack <= 1
  }

  #hasPackCards() {
    return this.querySelector('[data-pack-card]') != null
  }

  #isReady() {
    const target = this.#targetCount()
    return target > 0 && this.#total() === target
  }

  #targetCount() {
    if (this.#hasPackCards()) return this.#currentPack
    return this.#maxSelection()
  }

  #addButton() {
    return this.#section?.querySelector('[ref="addToCartButton"]') ?? null
  }

  #buttonLabel(button) {
    const content = button.querySelector('.button__content')
    if (!content) return null
    const spans = [...content.querySelectorAll(':scope > span')]
    return (
      spans.find(
        (span) =>
          !(span.classList.contains('icon') || span.querySelector('svg'))
      ) ?? null
    )
  }

  #inputFor(target) {
    return target
      .closest('[data-bundle-item]')
      ?.querySelector('[data-flavor-input]')
  }

  #inputs() {
    return [...this.querySelectorAll('[data-flavor-input]')]
  }

  #total() {
    return this.#inputs().reduce((sum, input) => sum + this.#value(input), 0)
  }

  #value(input) {
    const value = Number.parseInt(input.value, 10)
    return Number.isNaN(value) ? 0 : value
  }

  #stockMax(input) {
    const max = Number.parseInt(input.max, 10)
    return Number.isNaN(max) ? Number.POSITIVE_INFINITY : max
  }

  #maxSelection() {
    const max = Number.parseInt(this.dataset.maxSelection, 10)
    return Number.isNaN(max) ? 0 : max
  }

  #unitPrice() {
    const value = Number.parseInt(this.dataset.unitPrice ?? '', 10)
    return Number.isNaN(value) ? 0 : value
  }

  #unitCompare() {
    const value = Number.parseInt(this.dataset.unitCompare ?? '', 10)
    return Number.isNaN(value) ? 0 : value
  }

  #toggle(button, disabled) {
    if (!button) return
    button.disabled = disabled
    button.classList.toggle('disabled', disabled)
  }
}

if (!customElements.get('ui-custom-quantity')) {
  customElements.define('ui-custom-quantity', CustomQuantityComponent)
}
